import React, { Component } from 'react';
import * as PropTypes from 'prop-types';
import { Button, ButtonGroup, Nav } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import './Tree.sass';
import {
  faArrowLeft,
  faArrowRight,
  faFile,
  faSpinnerThird,
} from '@whitebox-learning/fontawesome-pro-light-svg-icons';

import {
  ResearchDocumentIcon,
  ResearchActivityIcon,
  ResearchTutorialIcon,
} from '../Common/Icons';

const ResearchPageIcon = ({ category }) => {
  switch (category) {
    case 'tutorial':
      return <ResearchTutorialIcon />;
    case 'quiz':
    case 'worksheet':
      return <ResearchActivityIcon />;
    default:
      return <ResearchDocumentIcon />;
  }
};

const getAriaLabel = (page) => {
  const { name, category } = page;
  switch (category) {
    case 'tutorial':
      return `View tutorial: ${name}`;
    case 'quiz':
      return `View quiz: ${name}`;
    case 'worksheet':
      return `View worksheet: ${name}`;
    case 'spec':
      return `View design challenge: ${name}`;
    case 'build':
      return `View build activity: ${name}`;
    case 'lesson':
      return `View lesson: ${name}`;
    default:
      return `View: ${name}`;
  }
};

const Tree = ({
  title,
  exercises,

  isLoading,
  showHeadings,

  selected,
  setPage,
  nextPage,
  prevPage,
  enablePrev,
  enableNext,
}) => {
  const headings = [
    {
      title: 'Background',
      regex: /\w+\d\w01\d+/,
    },
    {
      title: 'Engineering Design Process',
      regex: /\w+\d\w02\d+/,
    },
    {
      title: 'Knowledge At Work',
      regex: /\w+\d\w03\d+/,
    },
    {
      title: 'Fabrication',
      regex: /\w+\d\w05\d+/,
    },
    {
      title: 'Evaluation',
      regex: /\w+\d\w06\d+/,
    },
  ];

  const pages = [];
  let previousHeading = null,
    currentHeading = null;
  for (const exercise of exercises) {
    if (showHeadings) {
      currentHeading = headings.find((h) => h.regex.test(exercise.code))?.title;
      if (currentHeading !== previousHeading) {
        pages.push({
          type: 'heading',
          title: currentHeading,
        });
        previousHeading = currentHeading;
      }
    }
    pages.push(exercise);
  }

  return (
    <div className="sidebar-nav d-flex flex-column" role="complementary" aria-label={`${title} navigation`}>
      {/* Title of the entire tree */}
      <h1 className="title">{title}</h1>

      {/* Navigation links and headers (for each link section) */}
      <Nav className="flex-column overflow-auto flex-nowrap">
        {pages.map((p, i) =>
          p.type && p.type === 'heading' ? (
            <Nav.Item key={i} className="nav-header mb-1">
              {p.title}
            </Nav.Item>
          ) : (
            <Nav.Item key={i}>
              <Nav.Link
                onClick={() => setPage(p)}
                className="d-flex justify-content-between"
              >
                {p.category === 'tutorial' ? (
                  <span className="fw-bold">{p.name}</span>
                ) : (
                  <span>{p.name}</span>
                )}

                {/* @todo Add back loading state + different icon types */}
                <Button
                  variant={selected === p.code ? 'wb' : 'sidebar'}
                  size="sm"
                  aria-label={getAriaLabel(p)}
                >
                  <ResearchPageIcon category={p.category} />
                </Button>
              </Nav.Link>
            </Nav.Item>
          )
        )}
      </Nav>

      {/* Next+Prev buttons at the bottom of the sidebar */}
      <div className="box box-min box-col p-1 sidebar-nav-buttons mt-auto">
        <ButtonGroup>
          <Button
            onClick={prevPage}
            disabled={!enablePrev}
            variant="secondary"
            className="w-100"
          >
            <FontAwesomeIcon icon={faArrowLeft} /> Previous
          </Button>
          <Button
            onClick={nextPage}
            disabled={!enableNext}
            variant="secondary"
            className="w-100"
          >
            Next <FontAwesomeIcon icon={faArrowRight} />
          </Button>
        </ButtonGroup>
      </div>
    </div>
  );
};

Tree.propTypes = {
  title: PropTypes.string.isRequired,
  exercises: PropTypes.array.isRequired,

  isLoading: PropTypes.bool,
  showHeadings: PropTypes.bool,

  selected: PropTypes.string, // selected page
  setPage: PropTypes.func.isRequired,
  nextPage: PropTypes.func,
  prevPage: PropTypes.func,
  enableNext: PropTypes.bool,
  enablePrev: PropTypes.bool,
};
Tree.defaultProps = {
  title: 'Menu',
  exercises: [],

  isLoading: false,
  showHeadings: true,

  selected: '',
  enableNext: false,
  enablePrev: false,
};

export default Tree;
